import React, { useState } from "react";
import MUIDataTable from "mui-datatables";
import { useTitle } from "@dyl/systems/TitleManager";
import { useSnackbar } from "notistack";
import { LevelText } from "@dyl/systems/LevelsManager";
import useAsync from "@dyl/hooks/useAsync";
import { getTasks } from "@dyl/apiClients/backendApi";
import startCase from "lodash/startCase";
import {
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import getTestsComponentsByTask from "@dyl/apiClients/backendApi/getTestsComponentsByTask";
import quickDateFormat from "@dyl/utils/quickDateFormat";
import withLevel from "@dyl/utils/withLevel";

const columns = [
    {
        name: 'task_id',
        options: { display: 'excluded' }
    },
    {
        label: 'Task ID',
        name: 'task_id_name'
    },
    {
        label: 'Skill',
        name: 'skill'
    },
    {
        label: 'Level',
        name: 'level'
    },
    {
        label: 'Task Type',
        name: 'task_type'
    },
    {
        label: 'Last modified',
        name: 'last_modified'
    }
];

export default withLevel(ExpandableRowTable);

function ExpandableRowTable({ globalLevel }) {
    useTitle('Anchor Tasks');
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [triggerReload] = useState();

    const tasks = useAsync(async () => {
        setLoading(true);
        try {
            const res = await getTasks(globalLevel.level_id, true);
            return res.map(x => {
                return {
                    task_id: x.task_id,
                    task_id_name: x.task_id_name,
                    skill: startCase(x.skill_id),
                    level: (<LevelText levelId={x.level_id} />),
                    task_type: x.description,
                    last_modified: quickDateFormat(x.updated_at)
                };
            });
        } catch {
            enqueueSnackbar('Failed to load tasks', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [triggerReload, globalLevel]) ?? [];

    const options = {
        download: false,
        selectableRows: 'none',

        textLabels: {
            body: {
                noMatch: loading ?
                    <CircularProgress /> :
                    'Sorry, there is no matching data to display',
            },
        },

        filter: true,
        filterType: "dropdown",
        responsive: "simple",
        rowsPerPage: 10,
        expandableRows: true,
        renderExpandableRow: (rowData) => {
            return (
                <RowDetails task_id={rowData[0]} colSpan={rowData.length + 1} />
            );
        }
    };

    return (
        <MUIDataTable
            title={"Anchor Tasks"}
            data={tasks}
            columns={columns}
            options={options}
        />
    );
};


function RowDetails({ task_id }) {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(true);

    const taskComponents = useAsync(async () => {
        setIsLoading(true);
        try {
            const res = await getTestsComponentsByTask(task_id)
            return res.map(x => ({
                test_name: x.test_name,
                skill_name: startCase(x.skill_id),
                last_modified: quickDateFormat(x.updated_at)
            }));
        } catch {
            enqueueSnackbar('Failed to load task components', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [task_id]) ?? [];

    return (
        <TableRow>
            <TableCell colSpan={15} >
                {isLoading
                    ? <div style={{ textAlign: 'center' }}> <CircularProgress/> </div>
                    : <>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Component Name</TableCell>
                                        <TableCell>Skill</TableCell>
                                        <TableCell>Last Modified</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {taskComponents.map(row => (
                                        <TableRow key={row.test_name}>
                                            <TableCell>{row.test_name}</TableCell>
                                            <TableCell>{row.skill_name}</TableCell>
                                            <TableCell>{row.last_modified}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>}
            </TableCell>
        </TableRow>
    );
}
